import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import { Header } from "./components/Header";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

const API_URL = "/api";

export default function ApplicationChecklist() {
  const { id } = useParams();
  console.log("🔎 Route id:", id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [company, setCompany] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState([]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getTransactionDetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        console.log("📥 Full API Response:", data);

        // keep transaction & formData as before
        setTransactionData(data);
        setFormData(data);

        // map DocumentCheckList and mark rows that should be default-grey
        const mappedChecklist = (data?.DocumentCheckList || []).map((it) => {
          const redFlag = it?.RedFlag;
          // default-grey if RedFlag is a string that's empty or only whitespace (covers " " and "")
          const isDefaultGrey =
            typeof redFlag === "string" && redFlag.trim() === "";
          return {
            ...it,
            __defaultGrey: isDefaultGrey,
          };
        });

        setChecklistFormData(mappedChecklist);
        setCompany(data);

        console.log("Mapped checklist (with __defaultGrey):", mappedChecklist);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchCompanyDetails();
  }, [id]);

  const navigate = useNavigate();

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  function formatDateMM(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  function getDaysDifference(targetDateString) {
    const today = new Date();
    const targetDate = new Date(targetDateString);
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);
    const diffInMs = targetDate - today;
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays;
  }

  const handleCancel = () => {
    setFormData(transactionData);
    setChecklistFormData(transactionData?.DocumentCheckList || []);
    setIsEditing(false);
  };

  function formatDateToDDMMYYYY(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const renderStatusCell = (section) => {
    let value = formData?.[section]?.STATUS || "";
    if (section === "ENTEREXPIRYDATE") value = "";
    return isEditing ? (
      <select
        className="form-select"
        value={value}
        onChange={(e) => handleChange(e, section, "STATUS")}
      >
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="N/A">N/A</option>
      </select>
    ) : (
      <div
        style={{
          backgroundColor: value === "Yes" ? "#58a65c" : "#fdf3d0",
          color: value === "Yes" ? "#fff" : "#000",
          textAlign: "center",
        }}
      >
        {value || ""}
      </div>
    );
  };

  const criteriaLabels = {
    COMPANYNAMEISCORRECT: "Company name is correct",
    THEREGISTRATIONISCURRENT: "The registration is current",
    THERETURNCOVERSTHEMOSTRECENTPERIOD:
      "The return covers the most recent period",
    SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED:
      "Shareholder(s) match organization chart (if provided)",
    THECHARTISSIGNEDANDDATED: "The chart is signed and dated",
    STATEMENTSARESIGNEDBYAUDITOR: "Statements are signed by auditor",
  };

  const renderCheckCell = (section) => {
    // if (section === "SUBMITTEDBUSINESSREGISTRATION") return "";

    // Show the label for these fields instead of CHECKINGDETAILS
    if (
      section === "COMPANYNAMEISCORRECT" ||
      section === "THEREGISTRATIONISCURRENT" ||
      section === "THERETURNCOVERSTHEMOSTRECENTPERIOD" ||
      section === "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED" ||
      section === "THECHARTISSIGNEDANDDATED" ||
      section === "STATEMENTSARESIGNEDBYAUDITOR"
    ) {
      return criteriaLabels[section] || "";
    }

    if (section === "ENTEREXPIRYDATE") {
      const rawDate = formData?.ENTEREXPIRYDATE?.CHECKINGDETAILS;
      return rawDate ? formatDate(rawDate) : "";
    }

    return formData?.[section]?.CHECKINGDETAILS || "";
  };

  const renderRedFlagCell = (section) => {
    const status = formData?.[section]?.STATUS;
    const isMandatory = formData?.[section]?.ISMENDATORY === true;

    if (isMandatory && status === "No") {
      return "Red Flag"; // show red flag
    }

    if (isMandatory && status === "Yes") {
      return ""; // no red flag text, but grey background
    }

    return ""; // default
  };

  const documentFields = [
    {
      label: "Submitted Business registration",
      key: "SUBMITTEDBUSINESSREGISTRATION",
    },
    {
      label: "Company name is correct",
      key: "COMPANYNAMEISCORRECT",
    },
    {
      label: "The registration is current",
      key: "THEREGISTRATIONISCURRENT",
    },
    { label: "Enter expiry date", key: "ENTEREXPIRYDATE", isDateDiff: true },
    {
      label: "Submitted Annual Return (NAR1)",
      key: "SUBMITTEDANNUALRETURN_NAR1",
    },
    {
      label: "Company name is correct",
      key: "COMPANYNAMEISCORRECT",
    },
    {
      label: "The return covers the most recent period",
      key: "THERETURNCOVERSTHEMOSTRECENTPERIOD",
    },
    {
      label: "Shareholder(s) match organization chart (if provided)",
      key: "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED",
    },
    {
      label: "Submitted Certificate of Incorporation",
      key: "SUBMITTEDCERTIFICATEOFINCORPORATION",
    },
    {
      label: "Submitted Memorandum & Articles",
      key: "SUBMITTEDMEMORANDUMANDARTICLES",
    },
    {
      label: "Submitted Organization Chart (if applicable)",
      key: "SUBMITTEDORGANIZATIONCHART_IFAPPLICABLE",
    },
    {
      label: "The chart is signed and dated",
      key: "THECHARTISSIGNEDANDDATED",
    },
    {
      label: "Submitted Financial statements (Audited) for the last 3 years",
      key: "SUBMITTEDFINANCIALSTATEMENTS_AUDITEDFORTHELAST3YEARS",
    },
    {
      label: "Statements are signed by auditor",
      key: "STATEMENTSARESIGNEDBYAUDITOR",
    },
    {
      label:
        "Submitted Financial statements (Audited) for at least 1 of the last 2 FYs",
      key: "SUBMITTEDFINANCIALSTATEMENTS_AUDITEDFORATLEAST1OFTHELAST2FYS",
    },
    {
      label:
        "Submitted Unaudited Management Accounts for the most recent period (that are error-free)",
      key: "SUBMITTEDUNAUDITEDMANAGEMENTACCOUNTSFORTHEMOSTRECENTPERIOD_THATAREERROR_FREE",
    },
    {
      label:
        "Submitted Unaudited Management Accounts for the most recent period",
      key: "SUBMITTEDUNAUDITEDMANAGEMENTACCOUNTSFORTHEMOSTRECENTPERIOD",
    },
    {
      label: "Submitted Bank Account Information",
      key: "SUBMITTEDBANKACCOUNTINFORMATION",
    },
    {
      label:
        "Submitted all Bank Account statements for the last 3 months (required for loans)",
      key: "SUBMITTEDALLBANKACCOUNTSTATEMENTSFORTHELAST3MONTHS_REQUIREDFORLOANS",
    },
    {
      label:
        "Submitted Evidence of Signatory authorisation (in case signatory is not a Director of the company)",
      key: "SUBMITTEDEVIDENCEOFSIGNATORYAUTHORISATION_INCASESIGNATORYISNOTADIRECTOROFTHECOMPANY",
    },
    {
      label:
        "Submitted list of ongoing trade disputes and litigation with an explanation for each (if applicable)",
      key: "SUBMITTEDLISTOFONGOINGTRADEDISPUTESANDLITIGATIONWITHANEXPLANATIONFOREACH_IFAPPLICABLE",
    },
  ];

  const filteredDocumentFields = documentFields.filter(
    (field) =>
      formData?.[field.key] !== undefined && formData?.[field.key] !== null
  );

  // existing documentFields array...
  const documentKeys = filteredDocumentFields.map((field) => field.key);
  // console.log("documentKeys", documentKeys);

  // get additional fields from formData that are not in documentFields
  const extraFields = Object.keys(formData || {}).filter(
    (key) => !documentKeys.includes(key)
  );
  // console.log("extraFields", extraFields);

  // merge documentFields with extra fields
  const allFields = [
    ...filteredDocumentFields,
    ...extraFields.map((key) => ({ label: key, key })),
  ];

  const hiddenCriteriaKeys = [
    "THEREGISTRATIONISCURRENT",
    "COMPANYNAMEISCORRECT",
    "THERETURNCOVERSTHEMOSTRECENTPERIOD",
    "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED",
    "THECHARTISSIGNEDANDDATED",
    "STATEMENTSARESIGNEDBYAUDITOR",
  ];

  const handleInputChange = (sectionKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { _id, ...rest } = formData;
    if (!_id) {
      alert("Missing _id");
      return;
    }
    // Remove any fields with undefined values
    const cleanedData = {};
    for (const key in rest) {
      if (rest[key] !== undefined) {
        cleanedData[key] = rest[key];
      }
    }
    const updatePayload = {
      _id: _id, // Make sure it's a string
      update_data: cleanedData,
    };

    try {
      const response = await fetch(
        "http://localhost:8000/riverchain/techop/v1.1/updateCompanyLog",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      const resultText = await response.text();

      if (!response.ok) {
        console.error("❌ Update failed:", resultText);
        alert("Update failed:\n" + resultText);
      } else {
        toast.success("Company log updated successfully");
      }
    } catch (error) {
      console.error("🚨 Network error:", error);
      toast.danger("An error occurred while updating the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <Header />
        </main>
      </div>

      <div className="container">
        <div className="row mt-4">
          <div className="col-md-12">
            <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
              Back to Transaction List
            </NavLink>

            <br />
            <br />
            <NavLink
              to={`/ViewTransactionList/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Loan Details
            </NavLink>

            <NavLink
              to={`/Drawdowns/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Drawdowns
            </NavLink>

            <NavLink
              to={`/ApplicationChecklist/${id}`}
              className="btn btn-dark ms-2"
            >
              Application Checklist
            </NavLink>

            <NavLink
              to={`/ScoreCard/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scorecard
            </NavLink>

            <NavLink
              to={`/TransactionSummary/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Transaction Summary
            </NavLink>

            <NavLink
              to={`/TermSheet/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Term Sheet
            </NavLink>

            <NavLink
              to={`/Payments/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Payments
            </NavLink>

            <NavLink
              to={`/InterestCalculations/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Interest Calculations
            </NavLink>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginTop: "15px" }}>Loan Information</h3>
              <div>
                {isEditing && (
                  <button
                    className="btn btn-secondary me-2 btn-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      setIsSubmitting(true);
                      await handleSubmit();
                      setIsSubmitting(false);
                    } else {
                      setFormData(transactionData);
                      setChecklistFormData(
                        transactionData?.DocumentCheckList || []
                      ); // checklist
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditing
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Edit"}
                </button>
              </div>
            </div>

            {loading || !transactionData ? (
              <ShimmerTable row={5} col={5} />
            ) : (
              <>
                <div className="col-md-12">
                  <table className="table table-bordered">
                    <tbody>
                      {[
                        { label: "Borrower:", key: "BORROWER" },
                        { label: "Loan amount (HK$)", key: "LOANAMOUNT_HK" },
                        { label: "Repayment", key: "REPAYMENT" },
                        { label: "Type", key: "TYPE" },
                        { label: "Max. Term (months)", key: "MAXTERM_MONTHS" },
                        {
                          label: "Interest rate:",
                          key: "INTERESTRATE",
                          format: "percent",
                        },
                        {
                          label: "Admin fee:",
                          key: "ADMINFEE",
                          format: "percent",
                        },
                        {
                          label: "Total Expected Revenue (HK$):",
                          key: "TOTALEXPECTEDREVENUE_HK",
                          format: "integer",
                        },
                        {
                          label: "Total Fees (HK$):",
                          key: "TOTALFEES_HK",
                          format: "integer",
                        },
                        {
                          label: "Total Interest (HK$):",
                          key: "TOTALINTEREST_HK",
                          format: "integer",
                        },
                      ].map(({ label, key, format }, index) => {
                        let value = transactionData?.[key];

                        if (format === "percent" && value != null) {
                          value = `${(parseFloat(value) * 100).toFixed(2)}%`;
                        } else if (format === "number" && value != null) {
                          value = parseFloat(value).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });
                        } else if (format === "integer" && value != null) {
                          value = Math.round(parseFloat(value)).toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 }
                          );
                        }

                        return (
                          <tr key={index}>
                            <td style={{ width: "50%" }}>{label}</td>
                            <td style={{ textAlign: "right", width: "50%" }}>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ textAlign: "right" }}
                                  value={formData?.[key] ?? ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                value ?? ""
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#173660",
                          color: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <th>Criteria</th>
                        <th>Checking Details</th>
                        <th>Input</th>
                        <th>Comments</th>
                        <th>Red Flag</th>
                        <th>Reviewer Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(checklistFormData) &&
                        checklistFormData.map((item, index) => {
                          const inputValue = item.Input || "";
                          const redFlagValue = item.RedFlag;
                          const isDefaultGrey =
                            item.__defaultGrey === true ||
                            (typeof redFlagValue === "string" &&
                              redFlagValue.trim() === "");

                          let redFlagBg = "white";
                          let redFlagText = "";
                          let inputBg = "white";
                          let inputTextColor = "black";

                          // Default grey if originally grey and no input yet (keep original behavior)
                          if (isDefaultGrey && !inputValue) {
                            redFlagBg = "#d3d3d3";
                          }

                          // ---- INPUT COLORING: apply to ALL rows (moved outside isDefaultGrey) ----
                          if (inputValue === "Yes") {
                            // Yes -> green input
                            inputBg = "#4CAF50";
                            inputTextColor = "white";
                          } else if (inputValue === "No") {
                            // No -> grey input
                            inputBg = "#d3d3d3";
                            inputTextColor = "black";
                          } else if (inputValue === "N/A") {
                            // N/A -> white input
                            inputBg = "white";
                            inputTextColor = "black";
                          }

                          // ---- RED FLAG: keep your original logic (only when isDefaultGrey) ----
                          if (isDefaultGrey) {
                            if (inputValue === "Yes") {
                              redFlagBg = "#d3d3d3";
                            } else if (inputValue === "No") {
                              redFlagBg = "red";
                              redFlagText = "Red Flag";
                            } else if (inputValue === "N/A") {
                              redFlagBg = "#d3d3d3";
                            }
                          }

                          return (
                            <tr
                              key={`${
                                item.Criteria ||
                                item.CheckingDetails ||
                                "criteria"
                              }-${index}`}
                            >
                              {/* Criteria */}
                              <td>{item.Criteria || ""}</td>

                              {/* Checking Details */}
                              <td>{item.CheckingDetails || ""}</td>

                              {/* Input */}
                              <td
                                style={{
                                  backgroundColor: inputBg,
                                  color: inputTextColor,
                                  textAlign: "center",
                                  fontWeight: "bold",
                                }}
                              >
                                {isEditing ? (
                                  <select
                                    className="form-select"
                                    style={{
                                      backgroundColor: inputBg,
                                      color: inputTextColor,
                                      textAlign: "center",
                                      fontWeight: "bold",
                                    }}
                                    value={inputValue}
                                    onChange={(e) => {
                                      const newInput = e.target.value;
                                      const newData = [...checklistFormData];

                                      newData[index] = {
                                        ...item,
                                        Input: newInput,
                                        RedFlag:
                                          isDefaultGrey && newInput === "No"
                                            ? "Red Flag"
                                            : isDefaultGrey
                                            ? " "
                                            : item.RedFlag,
                                        __defaultGrey: isDefaultGrey,
                                      };

                                      setChecklistFormData(newData);
                                    }}
                                  >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                    <option value="N/A">N/A</option>
                                  </select>
                                ) : (
                                  inputValue
                                )}
                              </td>

                              {/* Comments */}
                              <td>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={item.Comments || ""}
                                    onChange={(e) => {
                                      const newData = [...checklistFormData];
                                      newData[index] = {
                                        ...item,
                                        Comments: e.target.value,
                                      };
                                      setChecklistFormData(newData);
                                    }}
                                  />
                                ) : (
                                  item.Comments || ""
                                )}
                              </td>

                              {/* Red Flag */}
                              <td
                                style={{
                                  backgroundColor: redFlagBg,
                                  color:
                                    redFlagBg === "red" ? "white" : "black",
                                  textAlign: "center",
                                  fontWeight: "bold",
                                }}
                              >
                                {redFlagText}
                              </td>

                              {/* Reviewer Check */}
                              <td>
                                {isEditing ? (
                                  <select
                                    className="form-select"
                                    value={item.ReviewerCheck || "N/A"}
                                    onChange={(e) => {
                                      const newData = [...checklistFormData];
                                      newData[index] = {
                                        ...item,
                                        ReviewerCheck: e.target.value,
                                      };
                                      setChecklistFormData(newData);
                                    }}
                                  >
                                    <option value="N/A">N/A</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </select>
                                ) : (
                                  item.ReviewerCheck || "N/A"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
